import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { errMsg } from "../lib/api";
import { useToast } from "../lib/toast";
import { AppShell, Sidebar, Topbar } from "../components/Layout";
import { Button, Card, Spinner, cx } from "../components/ui";
import { Stat } from "../components/PushLog";
import {
  PaperclipIcon,
  LinkIcon,
  UnlinkIcon,
  CheckIcon,
  AlertIcon,
  RefreshIcon,
  ChevronRightIcon,
} from "../components/ui/Icons";

/* ------------------------------------------------------------ shared pieces */

function StatusPill({ ok, children }) {
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-medium ring-1 ring-inset",
        ok
          ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
          : "bg-ink-100 text-ink-500 ring-ink-200"
      )}
    >
      <span
        className={cx(
          "h-1.5 w-1.5 rounded-full",
          ok ? "bg-emerald-500" : "bg-ink-400"
        )}
      />
      {children}
    </span>
  );
}

/**
 * One side of the connection. Both cards share this frame so "connected" looks
 * identical whichever system it refers to.
 */
function ConnectionCard({ logo, name, subtitle, connected, children }) {
  // public/xero.png isn't in the repo yet; drop one in and it appears here
  // automatically. Until then show a lettered tile rather than a broken image.
  const [logoBroken, setLogoBroken] = useState(false);

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-3.5 border-b border-ink-200/70 px-5 py-4">
        <span className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-xl bg-white p-1.5 ring-1 ring-ink-200">
          {logoBroken ? (
            <span className="text-[16px] font-bold text-ink-400">{name[0]}</span>
          ) : (
            <img
              src={logo}
              alt={name}
              onError={() => setLogoBroken(true)}
              className="h-full w-full object-contain"
            />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-[15px] font-semibold text-ink-900">{name}</h3>
          <p className="mt-0.5 truncate text-[12.5px] text-ink-500">{subtitle}</p>
        </div>
        <StatusPill ok={connected}>
          {connected ? "Connected" : "Not connected"}
        </StatusPill>
      </div>
      <div className="p-5">{children}</div>
    </Card>
  );
}

/** Radio-style org list used by both sides. */
function OrgOption({ selected, title, sub, onClick, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cx(
        "flex w-full items-center gap-3 rounded-xl border px-3.5 py-3 text-left transition",
        selected
          ? "border-brand-300 bg-brand-50/60 ring-1 ring-brand-200"
          : "border-ink-200 bg-white hover:border-ink-300 hover:bg-ink-50",
        disabled && "cursor-not-allowed opacity-60"
      )}
    >
      <span
        style={{ height: 18, width: 18 }}
        className={cx(
          "grid shrink-0 place-items-center rounded-full border-2 transition",
          selected ? "border-brand-500 bg-brand-500" : "border-ink-300"
        )}
      >
        {selected && <CheckIcon size={10} strokeWidth={3} className="text-white" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13.5px] font-medium text-ink-900">
          {title}
        </span>
        {sub && (
          <span className="mt-0.5 block truncate font-mono text-[11.5px] text-ink-500">
            {sub}
          </span>
        )}
      </span>
    </button>
  );
}

/* -------------------------------------------------------------------- page */

export default function Attachments() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [xero, setXero] = useState({ connected: false });
  const [xeroBusy, setXeroBusy] = useState(false);
  const [pickingTenant, setPickingTenant] = useState(false);

  const [files, setFiles] = useState([]);
  // Naqood is connected per file in this app, so "which org" is a choice of
  // file. Kept in the browser for now — nothing server-side consumes it until
  // the attachment logic lands.
  const [naqoodFileId, setNaqoodFileId] = useState(
    () => localStorage.getItem("attachmentsNaqoodFileId") || ""
  );

  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();

  const loadXero = useCallback(async () => {
    try {
      const { data } = await api.get("/api/xero/status");
      setXero(data);
      return data;
    } catch (err) {
      setXero({ connected: false });
      return null;
    }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("token")) {
      navigate("/");
      return;
    }
    Promise.all([api.get("/api/auth/me"), api.get("/api/files"), loadXero()])
      .then(([u, f]) => {
        setUser(u.data);
        setFiles(f.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [navigate, loadXero]);

  // Xero sends the browser back here with the outcome in the query string.
  useEffect(() => {
    const result = params.get("xero");
    if (!result) return;

    if (result === "connected") {
      const org = params.get("org");
      toast.success(
        "Xero connected",
        org ? `Signed in and using ${org}.` : "Now choose which organisation to use."
      );
    } else if (result === "error") {
      toast.error(
        "Xero connection failed",
        params.get("message") || "Xero did not complete the sign-in."
      );
    }
    // Cleared so a refresh doesn't replay the same toast.
    setParams({}, { replace: true });
    loadXero();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const connectXero = async () => {
    setXeroBusy(true);
    try {
      const { data } = await api.get("/api/xero/connect");
      window.location.href = data.url;
    } catch (err) {
      toast.error("Could not start Xero sign-in", errMsg(err));
      setXeroBusy(false);
    }
  };

  const disconnectXero = async () => {
    if (
      !window.confirm(
        "Disconnect Xero?\n\nThis signs this app out of Xero and revokes its access. " +
          "You can reconnect at any time."
      )
    )
      return;
    setXeroBusy(true);
    try {
      const { data } = await api.post("/api/xero/disconnect");
      if (data.warning) toast.error("Disconnected with a warning", data.warning);
      else toast.success("Xero disconnected", "This app no longer has access.");
      await loadXero();
    } catch (err) {
      toast.error("Could not disconnect", errMsg(err));
    } finally {
      setXeroBusy(false);
    }
  };

  const selectTenant = async (t) => {
    setXeroBusy(true);
    try {
      await api.post("/api/xero/select-tenant", {
        tenantId: t.tenantId,
        tenantName: t.tenantName,
      });
      setXero((p) => ({ ...p, tenantId: t.tenantId, tenantName: t.tenantName }));
      setPickingTenant(false);
      toast.success("Organisation selected", t.tenantName);
    } catch (err) {
      toast.error("Could not select organisation", errMsg(err));
    } finally {
      setXeroBusy(false);
    }
  };

  const chooseNaqoodFile = (f) => {
    setNaqoodFileId(f._id);
    localStorage.setItem("attachmentsNaqoodFileId", f._id);
  };

  const naqoodFiles = files.filter((f) => f.naqood?.isConnected);
  const naqoodFile = naqoodFiles.find((f) => f._id === naqoodFileId) || null;
  const xeroReady = xero.connected && Boolean(xero.tenantId);
  const bothReady = xeroReady && Boolean(naqoodFile);

  return (
    <AppShell sidebar={<Sidebar user={user} active="attachments" />}>
      <Topbar
        title="Attachments"
        subtitle="Connect Xero and Naqood, then move attachments between them"
      />

      <main className="flex-1 bg-mesh-light px-8 py-7">
        {loading ? (
          <div className="grid place-items-center py-24">
            <Spinner size={22} />
          </div>
        ) : (
          <div className="mx-auto max-w-5xl space-y-6">
            {/* Progress rail */}
            <Card className="flex flex-wrap items-center gap-x-3 gap-y-2 px-5 py-4">
              <PaperclipIcon size={17} className="shrink-0 text-brand-500" />
              <p className="text-[13.5px] font-semibold text-ink-900">
                Step 1 — connect both sides
              </p>
              <span className="ml-auto flex items-center gap-2">
                <StatusPill ok={xeroReady}>Xero</StatusPill>
                <ChevronRightIcon size={14} className="text-ink-300" />
                <StatusPill ok={Boolean(naqoodFile)}>Naqood</StatusPill>
              </span>
            </Card>

            <div className="grid gap-6 lg:grid-cols-2">
              {/* ----------------------------------------------------- Xero */}
              <ConnectionCard
                logo="/xero.png"
                name="Xero"
                subtitle={
                  xero.connected
                    ? xero.tenantName || "Choose an organisation"
                    : "Source of the attachments"
                }
                connected={xeroReady}
              >
                {!xero.connected ? (
                  <>
                    <p className="text-[13px] leading-relaxed text-ink-600">
                      Sign in to Xero to let this app read invoices, bills and the
                      files attached to them. You'll be sent to Xero's own login
                      page — no credentials are entered here.
                    </p>
                    <Button
                      className="mt-4 w-full"
                      icon={LinkIcon}
                      loading={xeroBusy}
                      onClick={connectXero}
                    >
                      Connect to Xero
                    </Button>
                  </>
                ) : (
                  <>
                    {xero.tenantError && (
                      <div className="mb-4 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-3">
                        <AlertIcon size={15} className="mt-0.5 shrink-0 text-amber-600" />
                        <p className="text-[12.5px] leading-relaxed text-amber-800">
                          {xero.tenantError}
                        </p>
                      </div>
                    )}

                    {xero.tenantId && !pickingTenant ? (
                      <div className="rounded-xl border border-ink-200 bg-ink-50/60 px-3.5 py-3">
                        <p className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-500">
                          Organisation
                        </p>
                        <p className="mt-1 truncate text-[14px] font-semibold text-ink-900">
                          {xero.tenantName}
                        </p>
                        {(xero.tenants?.length || 0) > 1 && (
                          <button
                            onClick={() => setPickingTenant(true)}
                            className="mt-1.5 text-[12.5px] font-medium text-brand-600 hover:text-brand-700"
                          >
                            Switch organisation
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-[12.5px] font-medium text-ink-700">
                          {xero.tenants?.length
                            ? "Which organisation?"
                            : "No organisations available on this login."}
                        </p>
                        {(xero.tenants || []).map((t) => (
                          <OrgOption
                            key={t.tenantId}
                            selected={t.tenantId === xero.tenantId}
                            title={t.tenantName}
                            onClick={() => selectTenant(t)}
                            disabled={xeroBusy}
                          />
                        ))}
                      </div>
                    )}

                    <div className="mt-4 flex gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        icon={RefreshIcon}
                        onClick={loadXero}
                      >
                        Refresh
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        icon={UnlinkIcon}
                        className="ml-auto !text-red-600 hover:!bg-red-50 hover:!ring-red-200"
                        loading={xeroBusy}
                        onClick={disconnectXero}
                      >
                        Disconnect
                      </Button>
                    </div>
                  </>
                )}
              </ConnectionCard>

              {/* --------------------------------------------------- Naqood */}
              <ConnectionCard
                logo="/naqood.png"
                name="Naqood"
                subtitle={
                  naqoodFile
                    ? naqoodFile.naqood.organizationSlug
                    : "Destination for the attachments"
                }
                connected={Boolean(naqoodFile)}
              >
                {naqoodFiles.length === 0 ? (
                  <>
                    <p className="text-[13px] leading-relaxed text-ink-600">
                      No Naqood organisation is connected yet. Connect one from
                      your files first — attachments are written into the same
                      organisation you push data to.
                    </p>
                    <Button
                      className="mt-4 w-full"
                      variant="secondary"
                      onClick={() => navigate("/dashboard")}
                    >
                      Go to files
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="mb-2 text-[12.5px] font-medium text-ink-700">
                      Which organisation?
                    </p>
                    <div className="max-h-64 space-y-2 overflow-y-auto">
                      {naqoodFiles.map((f) => (
                        <OrgOption
                          key={f._id}
                          selected={f._id === naqoodFileId}
                          title={f.fileName}
                          sub={f.naqood.organizationSlug}
                          onClick={() => chooseNaqoodFile(f)}
                        />
                      ))}
                    </div>
                  </>
                )}
              </ConnectionCard>
            </div>

            {/* Step 2 — the transfer */}
            <TransferPanel
              ready={bothReady}
              xeroOrg={xero.tenantName}
              naqoodFile={naqoodFile}
            />

            {/* Step 3 — journals / spend money / receive money */}
            <JournalTransferPanel
              ready={bothReady}
              xeroOrg={xero.tenantName}
              naqoodFile={naqoodFile}
            />
          </div>
        )}
      </main>
    </AppShell>
  );
}

/* ---------------------------------------------------------------- transfer */

const fmtBytes = (n) =>
  n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;

/**
 * Preview then transfer. The preview is read-only and uses the same resolution
 * code as the transfer, so what it lists is what will actually happen.
 */
function TransferPanel({ ready, xeroOrg, naqoodFile }) {
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState("");
  const toast = useToast();

  const load = async () => {
    setBusy("preview");
    setResult(null);
    try {
      const { data } = await api.get("/api/attachments/preview", {
        params: { fileId: naqoodFile._id },
      });
      setPreview(data);
      if (!data.matched) {
        toast.error("Nothing to transfer", "No Xero bill matched a Naqood purchase.");
      }
    } catch (err) {
      toast.error("Could not build the preview", errMsg(err));
    } finally {
      setBusy("");
    }
  };

  const transfer = async () => {
    const ok = window.confirm(
      `Attach ${preview.files} file(s) to ${preview.matched} purchase(s) in Naqood?\n\n` +
        `Xero:   ${preview.xeroOrg}\n` +
        `Naqood: ${preview.naqoodOrg}\n\n` +
        "This writes to Naqood."
    );
    if (!ok) return;

    setBusy("transfer");
    try {
      const { data } = await api.post("/api/attachments/transfer", {
        fileId: naqoodFile._id,
      });
      setResult(data);
      if (data.failedCount) {
        toast.error(
          `${data.success} attached, ${data.failedCount} failed`,
          "Open the list below for the reason on each one."
        );
      } else {
        toast.success(`${data.success} attachment(s) transferred`, "Every file went across.");
      }
    } catch (err) {
      toast.error("Transfer failed", errMsg(err));
    } finally {
      setBusy("");
    }
  };

  if (!ready) {
    return (
      <Card className="px-5 py-4">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-ink-100 text-[11px] font-bold text-ink-400">
            2
          </span>
          <div>
            <p className="text-[13.5px] font-semibold text-ink-900">
              Step 2 — move the attachments
            </p>
            <p className="mt-1 text-[12.5px] text-ink-600">
              Connect both sides above to unlock this.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 border-b border-ink-200/70 px-5 py-4">
        <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand-100 text-[11px] font-bold text-brand-700">
          2
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[13.5px] font-semibold text-ink-900">Move the attachments</p>
          <p className="mt-0.5 truncate text-[12.5px] text-ink-600">
            {xeroOrg} <span className="text-ink-400">→</span>{" "}
            {naqoodFile.naqood.organizationSlug}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            icon={RefreshIcon}
            loading={busy === "preview"}
            disabled={Boolean(busy)}
            onClick={load}
          >
            {preview ? "Refresh preview" : "Preview matches"}
          </Button>
          <Button
            icon={PaperclipIcon}
            loading={busy === "transfer"}
            disabled={Boolean(busy) || !preview?.files}
            onClick={transfer}
          >
            {preview?.files ? `Transfer ${preview.files} file(s)` : "Transfer"}
          </Button>
        </div>
      </div>

      <div className="p-5">
        {!preview && !result && (
          <p className="text-[13px] leading-relaxed text-ink-600">
            Preview first — it lists every file and the purchase it would attach
            to, without writing anything. The transfer button stays disabled
            until you have.
          </p>
        )}

        {preview && !result && (
          <>
            <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="Bills w/ files" value={preview.billsWithAttachments} />
              <Stat label="Matched" value={preview.matched} tone="success" />
              <Stat
                label="Unmatched"
                value={preview.unmatched.length}
                tone={preview.unmatched.length ? "danger" : "muted"}
              />
              <Stat label="Files" value={preview.files} />
            </div>

            {preview.unmatched.length > 0 && (
              <div className="mb-4 rounded-xl border border-red-200 bg-red-50/60 px-4 py-3">
                <p className="text-[13px] font-semibold text-red-800">
                  {preview.unmatched.length} bill(s) could not be matched — these
                  will be skipped
                </p>
                <ul className="mt-2 space-y-1.5">
                  {preview.unmatched.slice(0, 8).map((u, i) => (
                    <li key={i} className="text-[12.5px] leading-relaxed text-red-700">
                      <span className="font-medium">{u.billNumber}</span> — {u.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="overflow-hidden rounded-xl border border-ink-200">
              <div className="border-b border-ink-200 bg-ink-50 px-4 py-2.5">
                <p className="text-[12.5px] font-semibold text-ink-800">
                  Will attach {preview.files} file(s) · {fmtBytes(preview.bytes)}
                </p>
              </div>
              <ul className="max-h-80 divide-y divide-ink-100 overflow-y-auto bg-white">
                {preview.rows.map((r, i) => (
                  <li key={i} className="flex gap-3 px-4 py-2.5">
                    <PaperclipIcon size={14} className="mt-0.5 shrink-0 text-ink-400" />
                    <div className="min-w-0 flex-1">
                      <p className="break-words text-[12.5px] font-medium text-ink-900">
                        {r.fileName}
                      </p>
                      <p className="mt-0.5 truncate font-mono text-[11.5px] text-ink-500">
                        {r.billNumber} → {r.naqoodRef}
                      </p>
                    </div>
                    <span className="shrink-0 text-[11.5px] tabular-nums text-ink-400">
                      {fmtBytes(r.sizeBytes)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}

        {result && <TransferResult result={result} />}
      </div>
    </Card>
  );
}

/* ---------------------------------------------------- journal transfer */

/**
 * Same shape as TransferPanel, pointed at Manual Journals / Spend Money /
 * Receive Money instead of Bills — those land in Naqood as plain journal
 * entries, matched by a "[TAG]" in the line description rather than a ref
 * field, so it's a separate step rather than folded into Step 2.
 * TransferResult below is shared as-is: both routes emit the same
 * {billNumber, naqoodRef, fileName, message} row shape.
 */
function JournalTransferPanel({ ready, xeroOrg, naqoodFile }) {
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState("");
  const toast = useToast();

  const load = async () => {
    setBusy("preview");
    setResult(null);
    try {
      const { data } = await api.get("/api/journal-attachments/preview", {
        params: { fileId: naqoodFile._id },
      });
      setPreview(data);
      if (!data.matched) {
        toast.error(
          "Nothing to transfer",
          "No Xero journal / spend / receive money matched a Naqood journal entry."
        );
      }
    } catch (err) {
      toast.error("Could not build the preview", errMsg(err));
    } finally {
      setBusy("");
    }
  };

  const transfer = async () => {
    const ok = window.confirm(
      `Attach ${preview.files} file(s) to ${preview.matched} journal entr${preview.matched === 1 ? "y" : "ies"} in Naqood?\n\n` +
        `Xero:   ${preview.xeroOrg}\n` +
        `Naqood: ${preview.naqoodOrg}\n\n` +
        "This writes to Naqood."
    );
    if (!ok) return;

    setBusy("transfer");
    try {
      const { data } = await api.post("/api/journal-attachments/transfer", {
        fileId: naqoodFile._id,
      });
      setResult(data);
      if (data.failedCount) {
        toast.error(
          `${data.success} attached, ${data.failedCount} failed`,
          "Open the list below for the reason on each one."
        );
      } else {
        toast.success(`${data.success} attachment(s) transferred`, "Every file went across.");
      }
    } catch (err) {
      toast.error("Transfer failed", errMsg(err));
    } finally {
      setBusy("");
    }
  };

  if (!ready) return null; // Step 2's locked-state card already explains this.

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 border-b border-ink-200/70 px-5 py-4">
        <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand-100 text-[11px] font-bold text-brand-700">
          3
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[13.5px] font-semibold text-ink-900">
            Move journal / spend / receive money attachments
          </p>
          <p className="mt-0.5 truncate text-[12.5px] text-ink-600">
            {xeroOrg} <span className="text-ink-400">→</span>{" "}
            {naqoodFile.naqood.organizationSlug}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            icon={RefreshIcon}
            loading={busy === "preview"}
            disabled={Boolean(busy)}
            onClick={load}
          >
            {preview ? "Refresh preview" : "Preview matches"}
          </Button>
          <Button
            icon={PaperclipIcon}
            loading={busy === "transfer"}
            disabled={Boolean(busy) || !preview?.files}
            onClick={transfer}
          >
            {preview?.files ? `Transfer ${preview.files} file(s)` : "Transfer"}
          </Button>
        </div>
      </div>

      <div className="p-5">
        {!preview && !result && (
          <p className="text-[13px] leading-relaxed text-ink-600">
            Manual Journals, Spend Money and Receive Money have no ref field in
            Naqood — they're matched by a tag embedded in the line description
            instead. Preview first to see exactly what would attach.
          </p>
        )}

        {preview && !result && (
          <>
            <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="Sources w/ files" value={preview.sourcesWithAttachments} />
              <Stat label="Matched" value={preview.matched} tone="success" />
              <Stat
                label="Unmatched"
                value={preview.unmatched.length}
                tone={preview.unmatched.length ? "danger" : "muted"}
              />
              <Stat label="Files" value={preview.files} />
            </div>

            {preview.unmatched.length > 0 && (
              <div className="mb-4 rounded-xl border border-red-200 bg-red-50/60 px-4 py-3">
                <p className="text-[13px] font-semibold text-red-800">
                  {preview.unmatched.length} source(s) could not be matched —
                  these will be skipped
                </p>
                <ul className="mt-2 space-y-1.5">
                  {preview.unmatched.slice(0, 8).map((u, i) => (
                    <li key={i} className="text-[12.5px] leading-relaxed text-red-700">
                      <span className="font-medium">{u.billNumber}</span> — {u.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="overflow-hidden rounded-xl border border-ink-200">
              <div className="border-b border-ink-200 bg-ink-50 px-4 py-2.5">
                <p className="text-[12.5px] font-semibold text-ink-800">
                  Will attach {preview.files} file(s) · {fmtBytes(preview.bytes)}
                </p>
              </div>
              <ul className="max-h-80 divide-y divide-ink-100 overflow-y-auto bg-white">
                {preview.rows.map((r, i) => (
                  <li key={i} className="flex gap-3 px-4 py-2.5">
                    <PaperclipIcon size={14} className="mt-0.5 shrink-0 text-ink-400" />
                    <div className="min-w-0 flex-1">
                      <p className="break-words text-[12.5px] font-medium text-ink-900">
                        {r.fileName}
                      </p>
                      <p className="mt-0.5 truncate font-mono text-[11.5px] text-ink-500">
                        {r.billNumber} → {r.naqoodRef}
                      </p>
                    </div>
                    <span className="shrink-0 text-[11.5px] tabular-nums text-ink-400">
                      {fmtBytes(r.sizeBytes)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}

        {result && <TransferResult result={result} />}
      </div>
    </Card>
  );
}

function TransferResult({ result }) {
  return (
    <div className="space-y-4">
      {result.integrityWarnings?.length > 0 && (
        <div className="overflow-hidden rounded-xl border-2 border-amber-400 bg-amber-50">
          <div className="flex items-start gap-2.5 px-4 py-3">
            <AlertIcon size={17} className="mt-0.5 shrink-0 text-amber-600" />
            <div>
              <p className="text-[13.5px] font-bold text-amber-900">
                {result.integrityWarnings.length} purchase(s) changed unexpectedly
              </p>
              <p className="mt-1 text-[12.5px] leading-relaxed text-amber-800">
                The file attached, but something else on the record moved. Check
                these in Naqood.
              </p>
            </div>
          </div>
          <ul className="divide-y divide-amber-200 bg-white">
            {result.integrityWarnings.map((w, i) => (
              <li key={i} className="px-4 py-2.5 text-[12.5px] text-amber-800">
                <span className="font-medium text-ink-900">{w.naqoodRef}</span> — {w.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Attached" value={result.success} tone="success" />
        <Stat
          label="Failed"
          value={result.failedCount}
          tone={result.failedCount ? "danger" : "muted"}
        />
        <Stat label="Unmatched" value={result.unmatched?.length || 0} tone="muted" />
        <Stat label="Transferred" value={fmtBytes(result.bytes || 0)} />
      </div>

      {result.failed?.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-red-200">
          <div className="border-b border-red-200 bg-red-50 px-4 py-2.5">
            <p className="text-[12.5px] font-semibold text-red-800">
              {result.failed.length} failure(s)
            </p>
          </div>
          <ul className="max-h-72 divide-y divide-ink-100 overflow-y-auto bg-white">
            {result.failed.map((f, i) => (
              <li key={i} className="px-4 py-2.5">
                <p className="text-[12.5px] font-medium text-ink-900">
                  {f.billNumber} {f.fileName ? `· ${f.fileName}` : ""}
                </p>
                <p className="mt-1 break-words text-[12.5px] leading-relaxed text-red-700">
                  {f.message}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.attached?.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-ink-200">
          <div className="border-b border-ink-200 bg-ink-50 px-4 py-2.5">
            <p className="text-[12.5px] font-semibold text-ink-800">
              {result.attached.length} attached
            </p>
          </div>
          <ul className="max-h-72 divide-y divide-ink-100 overflow-y-auto bg-white">
            {result.attached.map((a, i) => (
              <li key={i} className="flex gap-3 px-4 py-2.5">
                <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-emerald-100 text-emerald-600">
                  <CheckIcon size={12} strokeWidth={3} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="break-words text-[12.5px] text-ink-800">{a.fileName}</p>
                  <p className="mt-0.5 truncate font-mono text-[11.5px] text-ink-400">
                    {a.billNumber} → {a.naqoodRef}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
